import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_fdfa1102-6a78-496f-adc3-9918563c18d9/artifacts/a4oqwkvk_Propac%20logo.png";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Sign in · Propac | ODR Sys";
    }, []);

    if (user) {
        return (
            <Navigate
                to={user.must_change_password ? "/change-password" : "/dashboard"}
                replace
            />
        );
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const u = await login(username.trim(), password);
            navigate(u.must_change_password ? "/change-password" : "/dashboard");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--odr-bg)] px-4">
            <div
                className="w-full max-w-md bg-white border border-gray-200 rounded-sm"
                data-testid="login-card"
            >
                <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-5">
                    <img
                        src={LOGO_URL}
                        alt="Pro-Pac"
                        className="w-10 h-10 object-contain"
                    />
                    <div>
                        <div className="text-lg font-semibold text-gray-900">
                            Propac <span className="text-gray-400 font-normal">|</span> ODR Sys
                        </div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">
                            Pro-Pac Inc. · Operations
                        </div>
                    </div>
                </div>
                <form className="px-6 py-6 space-y-4" onSubmit={onSubmit}>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                            Username
                        </label>
                        <input
                            type="text"
                            autoFocus
                            autoComplete="username"
                            data-testid="login-username"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                            Password
                        </label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            data-testid="login-password"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>
                    {error && (
                        <div
                            className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2"
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="login-submit"
                        className="odr-btn w-full rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold uppercase tracking-wider transition-colors disabled:opacity-60"
                    >
                        {submitting ? "Signing in…" : "Sign in"}
                    </button>
                    <p className="text-xs text-gray-500 text-center pt-1">
                        Authorized personnel only · Propac | ODR Sys v0.1
                    </p>
                </form>
            </div>
        </div>
    );
}
